import { useState, useRef, useEffect } from 'react';
import { Terminal as TerminalIcon, Trash2 } from 'lucide-react';
import useStore from '../lib/store';
import { CommandProcessor } from '../lib/commands';

export default function Terminal() {
  const [input, setInput] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const outputRef = useRef(null);
  const inputRef = useRef(null);
  const commandProcessorRef = useRef(null);

  const selectedDevice = useStore(state => state.selectedDevice);
  const devices = useStore(state => state.devices);
  const simulator = useStore(state => state.simulator);
  const terminalOutput = useStore(state => state.terminalOutput);
  const addTerminalOutput = useStore(state => state.addTerminalOutput);
  const clearTerminal = useStore(state => state.clearTerminal);
  const addToHistory = useStore(state => state.addToHistory);
  const navigateHistory = useStore(state => state.navigateHistory);

  // Get live device from store
  const device = devices.find(d => d.id === selectedDevice?.id);

  // Create command processor when device changes
  useEffect(() => {
    if (device) {
      commandProcessorRef.current = new CommandProcessor(simulator, device);
    }
  }, [device?.id, simulator]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  // Focus input when device changes
  useEffect(() => {
    if (inputRef.current && device) {
      inputRef.current.focus();
    }
  }, [device?.id]);

  const handleCommand = (e) => {
    e.preventDefault();
    
    if (!input.trim() || !device) return;

    const command = input.trim();
    
    // Stop continuous ping if running
    if (commandProcessorRef.current?.continuousPing) {
      commandProcessorRef.current.stopContinuousPing();
      addTerminalOutput('\nContinuous ping stopped.', 'warning');
      setInput('');
      setIsExecuting(false);
      return;
    }

    // Show command in output
    addTerminalOutput(`${device.name}> ${command}`, 'prompt');
    addToHistory(command);
    setInput('');

    // Execute command
    if (commandProcessorRef.current) {
      setIsExecuting(true);

      const result = commandProcessorRef.current.execute(
        command,
        (text, type = 'info') => addTerminalOutput(text, type),
        (path) => {
          if (window.animatePacket) {
            window.animatePacket(path);
          }
        }
      );

      // Handle special commands
      if (result?.clear) {
        clearTerminal();
      }

      // Check if continuous ping started
      if (!commandProcessorRef.current?.continuousPing) {
        setIsExecuting(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const cmd = navigateHistory('up');
      if (cmd !== null) setInput(cmd);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const cmd = navigateHistory('down');
      if (cmd !== null) setInput(cmd);
    }
  };

  const handleClear = () => {
    // Stop continuous ping if running
    if (commandProcessorRef.current?.continuousPing) {
      commandProcessorRef.current.stopContinuousPing();
      addTerminalOutput('\nContinuous ping stopped.', 'warning');
    }
    clearTerminal();
  };

  if (!device) {
    return (
      <div className="terminal-container">
        <div className="terminal-header">
          <div className="terminal-title">
            <TerminalIcon size={16} />
            Terminal
          </div>
        </div>
        <div className="terminal-content">
          <div className="terminal-empty">
            <p>Select a device to use the terminal</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="terminal-container">
      <div className="terminal-header">
        <div className="terminal-title">
          <TerminalIcon size={16} />
          {device.name} Terminal
        </div>
        <button
          className="terminal-clear-btn"
          onClick={handleClear}
          title="Clear terminal"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="terminal-content" ref={outputRef}>
        <div className="terminal-output">
          {terminalOutput.map((line, index) => (
            <div key={index} className={`terminal-line ${line.type}`}>
              {line.text}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleCommand} className="terminal-input-container">
        <span className="terminal-prompt">{device.name}{'>'}</span>
        <input
          ref={inputRef}
          type="text"
          className="terminal-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isExecuting && !commandProcessorRef.current?.continuousPing}
          placeholder={
            commandProcessorRef.current?.continuousPing 
              ? "Press Enter to stop ping..." 
              : "Type 'help' for available commands"
          }
        />
      </form>
    </div>
  );
}
